import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react'
import { subscribeDayOrders } from '../services/orders'
import { useDayKey } from '../hooks/useDayKey'
import { useAuth } from './AuthContext'
import type { Order } from '../types'

interface Value {
  openTickets: Order[]
  /**
   * Günün bütün siparişleri (21 Eki 2026). Açık adisyonlar zaten bunlardan
   * süzülüyordu; kasa bilgisayarı "kasada basılacak" satışları ve Gel-Al
   * numarası için kapanmışları da görmeli. Ek sorgu yok, aynı abonelik.
   */
  todayOrders: Order[]
}

const OpenTicketsContext = createContext<Value | undefined>(undefined)

/**
 * Collates the way a person reading table names would.
 *
 * `numeric` is what makes "Masa 2" come before "Masa 10", plain string order
 * puts 10 first, which is exactly where someone hunting for a table stops
 * trusting the list. The Turkish locale keeps ç, ğ, ı, ö, ş, ü in their proper
 * places rather than after z.
 */
const collator = new Intl.Collator('tr', { numeric: true, sensitivity: 'base' })

/**
 * Sorted once here rather than in each screen, so the till and the waiter's
 * phone show the same order and a table is in the same place on both.
 *
 * Tickets with no table sink to the bottom: they are walk-up sales kept open,
 * and they have no name to look up.
 */
function sortByTable(tickets: Order[]): Order[] {
  return [...tickets].sort((a, b) => {
    const an = a.tableName ?? ''
    const bn = b.tableName ?? ''
    if (!an && !bn) return a.createdAtLocal - b.createdAtLocal
    if (!an) return 1
    if (!bn) return -1
    return collator.compare(an, bn)
  })
}

/**
 * Tickets waiting to be settled. Unlike the day's full order list this stays
 * small, a handful at a time, so watching it live is cheap, and both the
 * waiter and the till need it up to date the moment anything changes.
 */
export function OpenTicketsProvider({ children }: { children: ReactNode }) {
  const { tenantId, isAuthenticated } = useAuth()
  // Not toDayKey() read once: a device left open across midnight, or since
  // an earlier day, went on watching the day it started on, which is how
  // a waiter's phone ended up showing tables the till could not see.
  const dayKey = useDayKey()
  const [openTickets, setOpenTickets] = useState<Order[]>([])
  const [todayOrders, setTodayOrders] = useState<Order[]>([])

  useEffect(() => {
    if (!tenantId || !isAuthenticated) return
    return subscribeDayOrders(
      tenantId,
      dayKey,
      (all) => {
        setTodayOrders(all)
        // Filtered in memory rather than at the server: a ticket that gets
        // settled leaves the open set, and a filtered realtime subscription is
        // not told about rows that leave it.
        const tickets = all
          .filter((o) => o.status === 'open')
          .sort((a, b) => a.createdAtLocal - b.createdAtLocal)
        // How long a round took to travel from the waiter's device to here.
        // Both clocks are device clocks so this is approximate, but an order
        // arriving tens of seconds late shows up unmistakably.
        for (const t of tickets) {
          if (t.updatedAtLocal && !t.pending) {
            const lag = Date.now() - t.updatedAtLocal
            if (lag < 600_000) {
              console.info(`[tickets] ${t.tableName ?? t.id} arrived ${lag}ms after the edit`)
            }
          }
        }
        setOpenTickets(sortByTable(tickets))
      },
      (err) => console.error('[tickets]', err),
    )
  }, [tenantId, isAuthenticated, dayKey])

  const value = useMemo(() => ({ openTickets, todayOrders }), [openTickets, todayOrders])
  return <OpenTicketsContext.Provider value={value}>{children}</OpenTicketsContext.Provider>
}

export function useOpenTickets() {
  const ctx = useContext(OpenTicketsContext)
  if (!ctx) throw new Error('useOpenTickets must be used within an OpenTicketsProvider')
  return ctx
}
